import { Component, type ReactNode } from 'react';

interface State {
  hasError: boolean;
}

/**
 * Catches dynamic import (chunk) failures that happen when:
 * - A new deploy changes chunk hashes while the user has the old app open
 * - The browser requests old chunk URLs that no longer exist on the server
 *
 * On chunk error: reloads the page so the browser fetches the latest index.html
 * and gets the correct new chunk URLs.
 */
export class ChunkErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('Unable to preload CSS');

    if (isChunkError) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
